/**
 * Browser session id (GO-1577). The UI is the session originator: it mints a
 * stable `x-session-id` once per browser session and sends it on every request.
 * The BFF reads and forwards it; it never mints one.
 */

const SESSION_ID_KEY = "wrango.session_id";

/**
 * Cookie mirroring the session id. The `x-session-id` header rides only on tRPC
 * fetches; document/RSC *navigations* don't carry it, so the edge (`page_load`)
 * has no session to tag. A cookie rides every same-origin request incl.
 * navigations, so mirroring the id into one lets the edge read it (see
 * `edge-logging.ts`). Not a secret — a correlation id — and the `cookie` header is
 * redacted in logs regardless.
 */
export const SESSION_ID_COOKIE = "wrango_session_id";

/**
 * The stable session id for this browser session, minted on first use and
 * reused for the session's lifetime. Returns "" during SSR (the server caller
 * path does not use the browser links).
 */
export function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    const existing = window.sessionStorage.getItem(SESSION_ID_KEY);
    const id = existing ?? crypto.randomUUID();
    if (!existing) window.sessionStorage.setItem(SESSION_ID_KEY, id);
    mirrorToCookie(id);
    return id;
  } catch {
    // sessionStorage can throw (private mode / disabled storage). Fall back to a
    // volatile per-call id rather than breaking the request.
    return crypto.randomUUID();
  }
}

/** Session cookie (no Max-Age → cleared on browser close, matching session semantics). */
function mirrorToCookie(id: string): void {
  try {
    if (typeof document === "undefined") return;
    if (document.cookie.includes(`${SESSION_ID_COOKIE}=${id}`)) return; // already set
    const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${SESSION_ID_COOKIE}=${id}; path=/; SameSite=Lax${secure}`;
  } catch {
    // Cookie writes can throw in locked-down contexts; the session id still works
    // via the header path, only the edge loses its session tag.
  }
}
